<template>
  <div class="doc-validate">
    <div class="doc-validate__header">
      <button
        class="doc-validate__back"
        @click="$router.push({ name: 'documentos' })"
      >
        <i class="el-icon-arrow-left"></i>
      </button>
      <h2>Validar documento</h2>
    </div>

    <div class="doc-validate__body">
      <div class="dv-card">
        <h3 class="dv-card__title">Verificar firmas digitales</h3>
        <p class="dv-card__desc">
          Sube un documento PDF firmado para verificar la validez de sus firmas
          electrónicas.
        </p>

        <!-- Upload area -->
        <div
          v-if="!result"
          class="dv-upload"
          :class="{ 'dv-upload--active': dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf"
            style="display:none"
            @change="handleFileSelect"
          />
          <div v-if="!validating" class="dv-upload__content">
            <i class="el-icon-document-checked"></i>
            <span>Arrastra tu PDF aquí o haz clic para seleccionar</span>
          </div>
          <div v-else class="dv-upload__content">
            <i class="el-icon-loading"></i>
            <span>Validando firmas...</span>
          </div>
        </div>

        <!-- Results -->
        <div v-if="result" class="dv-results">
          <!-- Status badge -->
          <div class="dv-status" :class="statusClass">
            <i :class="statusIcon"></i>
            <span>{{ statusText }}</span>
          </div>

          <!-- File info -->
          <div v-if="fileName" class="dv-file-info">
            <i class="el-icon-document"></i>
            <span>{{ fileName }}</span>
          </div>

          <!-- Signatures -->
          <div v-if="result.has_signatures" class="dv-signatures">
            <h4>{{ result.signature_count }} firma(s) encontrada(s)</h4>

            <div
              v-for="(sig, idx) in result.signatures"
              :key="idx"
              class="dv-sig"
              :class="{
                'dv-sig--valid': sig.signature_valid,
                'dv-sig--invalid': !sig.signature_valid
              }"
            >
              <div class="dv-sig__header">
                <div
                  class="dv-sig__badge"
                  :class="
                    sig.signature_valid
                      ? 'dv-sig__badge--valid'
                      : 'dv-sig__badge--invalid'
                  "
                >
                  <i
                    :class="
                      sig.signature_valid
                        ? 'el-icon-circle-check'
                        : 'el-icon-circle-close'
                    "
                  ></i>
                </div>
                <div class="dv-sig__name">
                  <strong>{{
                    sig.signer_name || "Firma #" + sig.number
                  }}</strong>
                  <span v-if="sig.signing_time">{{ sig.signing_time }}</span>
                </div>
              </div>

              <div class="dv-sig__details">
                <div v-if="sig.signer_dn" class="dv-sig__row">
                  <span>DN:</span>
                  <code>{{ sig.signer_dn }}</code>
                </div>
                <div class="dv-sig__row">
                  <span>Firma válida:</span>
                  <strong
                    :style="{
                      color: sig.signature_valid ? '#1d9e75' : '#e74c3c'
                    }"
                  >
                    {{ sig.signature_valid ? "Sí" : "No" }}
                  </strong>
                </div>
                <!-- Certificado confiable — hidden until v2
                <div class="dv-sig__row">
                  <span>Certificado confiable:</span>
                  <strong :style="{ color: sig.certificate_trusted ? '#1d9e75' : '#e7a33c' }">
                    {{ sig.certificate_trusted ? 'Sí' : 'No verificado' }}
                  </strong>
                </div>
                -->
              </div>
            </div>
          </div>

          <!-- No signatures -->
          <div v-else-if="result.has_signatures === false" class="dv-no-sigs">
            <i class="el-icon-warning-outline"></i>
            <p>
              {{
                result.message || "El documento no contiene firmas digitales."
              }}
            </p>
          </div>

          <!-- Error message -->
          <div v-if="result.error" class="dv-error-msg">
            <i class="el-icon-warning"></i>
            <span>{{ result.error }}</span>
          </div>

          <!-- Try another -->
          <button
            class="md__btn-third-secondary"
            style="margin-top: 20px"
            @click="reset"
          >
            Validar otro documento
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import documentoService from "../services/documentoService";

export default {
  name: "DocumentValidate",
  data() {
    return {
      validating: false,
      dragOver: false,
      result: null,
      fileName: ""
    };
  },
  computed: {
    statusClass() {
      if (!this.result) return "";
      if (this.result.has_signatures && this.result.valid)
        return "dv-status--valid";
      if (this.result.has_signatures && !this.result.valid)
        return "dv-status--invalid";
      return "dv-status--none";
    },
    statusIcon() {
      if (!this.result) return "";
      if (this.result.has_signatures && this.result.valid)
        return "el-icon-circle-check";
      if (this.result.has_signatures && !this.result.valid)
        return "el-icon-circle-close";
      return "el-icon-info";
    },
    statusText() {
      if (!this.result) return "";
      if (this.result.has_signatures && this.result.valid)
        return "Todas las firmas son válidas";
      if (this.result.has_signatures && !this.result.valid)
        return "Se encontraron firmas inválidas";
      if (this.result.error) return "Error al validar";
      return "Sin firmas digitales";
    }
  },
  methods: {
    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.validateFile(file);
    },
    handleDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type === "application/pdf") this.validateFile(file);
    },
    async validateFile(file) {
      this.validating = true;
      this.result = null;
      this.fileName = file.name;
      try {
        const { data } = await documentoService.validateUploadedPdf(file);
        if (data.success) {
          this.result = data.data;
        } else {
          this.result = {
            valid: false,
            has_signatures: false,
            error: data.message,
            signatures: []
          };
        }
      } catch (e) {
        this.result = {
          valid: false,
          has_signatures: false,
          error: "Error al conectar con el servidor.",
          signatures: []
        };
      } finally {
        this.validating = false;
      }
    },
    reset() {
      this.result = null;
      this.fileName = "";
    }
  }
};
</script>

<style scoped>
.doc-validate {
  min-height: calc(100vh - 60px);
  background: #f7f8f9;
}

.doc-validate__header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  gap: 16px;
}

.doc-validate__header h2 {
  font-size: 18px;
  font-weight: 700;
  color: #31355c;
  margin: 0;
}

.doc-validate__back {
  width: 36px;
  height: 36px;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #31355c;
}

.doc-validate__body {
  padding: 24px;
  max-width: 640px;
  margin: 0 auto;
}

.dv-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 0 10px 0 #d6e4f3;
}

.dv-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #31355c;
  margin: 0 0 6px;
}

.dv-card__desc {
  font-size: 13px;
  color: #989aae;
  margin: 0 0 24px;
}

.dv-upload {
  border: 2px dashed #e4eaf6;
  border-radius: 12px;
  padding: 48px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.dv-upload:hover,
.dv-upload--active {
  border-color: #3fdbbf;
  background: rgba(63, 219, 191, 0.04);
}

.dv-upload__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #989aae;
}

.dv-upload__content i {
  font-size: 40px;
  color: #3fdbbf;
}

/* Status */
.dv-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 16px;
}

.dv-status--valid {
  background: rgba(29, 158, 117, 0.08);
  color: #1d9e75;
}

.dv-status--invalid {
  background: rgba(231, 76, 60, 0.08);
  color: #e74c3c;
}

.dv-status--none {
  background: rgba(152, 154, 174, 0.08);
  color: #989aae;
}

.dv-status i {
  font-size: 22px;
}

.dv-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f7f8f9;
  border-radius: 8px;
  font-size: 13px;
  color: #31355c;
  margin-bottom: 20px;
}

/* Signatures */
.dv-signatures h4 {
  font-size: 14px;
  color: #31355c;
  margin: 0 0 12px;
}

.dv-sig {
  border: 1px solid #e4eaf6;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.dv-sig--valid {
  border-left: 4px solid #1d9e75;
}

.dv-sig--invalid {
  border-left: 4px solid #e74c3c;
}

.dv-sig__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.dv-sig__badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.dv-sig__badge--valid {
  background: rgba(29, 158, 117, 0.12);
  color: #1d9e75;
}

.dv-sig__badge--invalid {
  background: rgba(231, 76, 60, 0.12);
  color: #e74c3c;
}

.dv-sig__name {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.dv-sig__name strong {
  font-size: 14px;
  color: #31355c;
}

.dv-sig__name span {
  font-size: 12px;
  color: #989aae;
}

.dv-sig__details {
  padding-left: 44px;
}

.dv-sig__row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
}

.dv-sig__row span {
  color: #989aae;
}

.dv-sig__row code {
  font-size: 10px;
  color: #666;
  word-break: break-all;
  max-width: 280px;
  text-align: right;
}

.dv-no-sigs {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px;
  color: #989aae;
  text-align: center;
}

.dv-no-sigs i {
  font-size: 40px;
}

.dv-error-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(231, 76, 60, 0.06);
  border-radius: 8px;
  color: #e74c3c;
  font-size: 13px;
  margin-top: 12px;
}

.md__btn-third-secondary {
  background: #f0f2f5;
  color: #31355c;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.md__btn-third-secondary:hover {
  background: #e4eaf6;
}
</style>
